import { useConfetti } from "@/components/Confetti";
import type { DealStage } from "@/contexts/DealStagesContext";
import { supabase } from "@/lib/supabase";
import type { Tables } from "@/types/database.types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryParams } from "./useQueryParams";
import { useRealtimeRefresh } from "./useRealtimeRefresh";

const WON_KIND = "won";

/** A role id (uuid); the employee's primary (first) role. */
type RoleId = string;
export type AssignmentEmployee = {
  id: string;
  name: string | null;
  nickname: string | null;
  role: RoleId | null;
  weekly_capacity_hours: number;
};

export type Assignment = Tables<"project_assignments"> & {
  employees: AssignmentEmployee[];
};

export type ProjectSummary = Tables<"projects"> & {
  // Deal stage embedded from deal_stages (joined via deal_stage_id, which lives on
  // Tables<"projects">). Special-case logic reads `deal_stage.kind`; display/forms
  // use `deal_stage.name`.
  deal_stage: DealStage | null;
  office: {
    name: string | null;
    is_active: boolean | null;
  } | null;
  project_assignments: Assignment[];
  /** Transient client-only state used while a newly created project is reconciled. */
  optimisticStatus?: "saving" | "created";
};

export interface UseProjectBoardDataResult {
  projects: ProjectSummary[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  addProjectOptimistically: (project: ProjectSummary) => void;
  confirmProjectOptimistically: (
    projectId: string,
    project: Tables<"projects">
  ) => void;
  removeProjectOptimistically: (projectId: string) => void;
  updateProjectOptimistically: (projectId: string, updates: Partial<ProjectSummary>) => void;
  refreshSilently: () => Promise<void>;
}

type RawProject = Tables<"projects"> & {
  deal_stage: DealStage | null;
  office: {
    name: string | null;
    is_active: boolean | null;
  } | null;
  project_assignments: Array<
    Tables<"project_assignments"> & {
      assignment_employees: Array<{
        employee: AssignmentEmployee | null;
      }> | null;
    }
  > | null;
};

function preservePendingProjects(
  nextProjects: ProjectSummary[],
  currentProjects: ProjectSummary[]
): ProjectSummary[] {
  const nextIds = new Set(nextProjects.map((project) => project.id));
  const pending = currentProjects.filter(
    (project) => project.optimisticStatus === "saving" && !nextIds.has(project.id)
  );
  return pending.length > 0 ? [...pending, ...nextProjects] : nextProjects;
}

export function useProjectBoardData(): UseProjectBoardDataResult {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const isInitialLoad = useRef(true);
  const optimisticStatusTimers = useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map());
  const { celebrate } = useConfetti();

  useEffect(() => {
    return () => {
      for (const timer of optimisticStatusTimers.current.values()) {
        clearTimeout(timer);
      }
      optimisticStatusTimers.current.clear();
    };
  }, []);

  // Last known deal-stage kind per project id, used to detect transitions into
  // the "won" stage. null until the first load completes so we don't fire
  // confetti for projects that were already won when the page opened.
  const prevStagesRef = useRef<Map<string, string | null> | null>(null);

  // Compares the freshly loaded projects against the previously seen stage kinds
  // and fires confetti for any project that just moved into the won stage. Because
  // the Supabase realtime echo reaches the editing user too — and this ref only
  // updates here, not on optimistic edits — this fires exactly once for the
  // actor and once for every other connected user.
  const celebrateWonTransitions = useCallback(
    (next: ProjectSummary[]) => {
      const prev = prevStagesRef.current;
      const nextStages = new Map<string, string | null>(
        next.map((p) => [p.id, p.deal_stage?.kind ?? null])
      );

      if (prev !== null) {
        for (const project of next) {
          if (
            project.deal_stage?.kind === WON_KIND &&
            prev.get(project.id) !== WON_KIND
          ) {
            celebrate();
            break;
          }
        }
      }

      prevStagesRef.current = nextStages;
    },
    [celebrate]
  );

  const { get } = useQueryParams();
  const projectParam = get("project");
  const focusedProjectId =
    typeof projectParam === "string" && /^[0-9a-zA-Z-]+$/.test(projectParam)
      ? projectParam
      : null;

  const fetchProjects = useCallback(async () => {
    // Preserve scroll position before refresh (only if not initial load)
    const scrollPosition = isInitialLoad.current
      ? undefined
      : (window.scrollY || window.pageYOffset);

    if (isInitialLoad.current) {
      isInitialLoad.current = false;
    }

    setLoading(true);

    // Fetch all projects with their stage embedded; lost projects are excluded
    // client-side below (except the focused project from the URL).
    let query = supabase
      .from("projects")
      .select(
        `
        id,
        name,
        color,
        budget,
        category_id,
        deal_stage_id,
        deal_stage:deal_stages ( id, name, likelihood_to_close, kind ),
        likelihood_to_close,
        start_date,
        end_date,
        num_weeks,
        office_id,
        client,
        client_id,
        description,
        potential_future_work,
        tech_stack,
        engagement_type,
        team_success_ingredients,
        created_at,
        updated_at,
        office:offices ( name, is_active ),
        project_assignments (
          id,
          project_id,
          required_role_id,
          weekly_hours_allocated,
          is_confirmed,
          notes,
          display_order,
          created_at,
          updated_at,
          assignment_employees (
            employee:employees ( id, name, nickname, role, weekly_capacity_hours )
          )
        )
      `
      );

    // Lost projects are excluded client-side after normalization (the deal stage
    // name is no longer a column on projects — it's joined from deal_stages).

    const { data, error: queryError } = await query
      .returns<RawProject[]>()
      .order("start_date", { ascending: true });

    if (queryError) {
      setError(queryError.message ?? "Failed to load projects");
      setProjects([]);
      setLoading(false);
      return;
    }

    const normalized: ProjectSummary[] = (data ?? []).map((project) => {
      const { project_assignments, ...projectFields } = project;
      const sortedAssignments = [...(project_assignments ?? [])].sort((a, b) => {
        const orderDiff = (a.display_order ?? 0) - (b.display_order ?? 0);
        if (orderDiff !== 0) return orderDiff;
        return (a.created_at ?? "").localeCompare(b.created_at ?? "");
      });
      return {
        ...projectFields,
        project_assignments:
          sortedAssignments.map((assignment) => {
            const { assignment_employees, ...assignmentFields } = assignment;
            const employees = (assignment_employees ?? [])
              .map((ae) => ae.employee)
              .filter((emp): emp is AssignmentEmployee => emp !== null)
              .map((emp) => {
                const r = emp.role;
                const primaryRole = Array.isArray(r) ? (r[0] ?? null) : (r ?? null);
                return {
                  id: emp.id,
                  name: emp.name ?? null,
                  nickname: emp.nickname ?? null,
                  role: primaryRole,
                  weekly_capacity_hours: emp.weekly_capacity_hours ?? 40,
                };
              });

            return {
              ...assignmentFields,
              employees,
            };
          }),
      };
    });

    const visible = normalized.filter(
      (p) => p.deal_stage?.kind !== "lost" || p.id === focusedProjectId
    );

    celebrateWonTransitions(visible);
    setProjects((currentProjects) =>
      preservePendingProjects(visible, currentProjects)
    );
    setError(null);
    setLoading(false);

    // Restore scroll position after React has finished rendering (only on refresh)
    if (scrollPosition !== undefined) {
      // Use double requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollPosition);
        });
      });
    }
  }, [focusedProjectId, celebrateWonTransitions]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const addProjectOptimistically = useCallback((project: ProjectSummary) => {
    setProjects((prevProjects) => [
      project,
      ...prevProjects.filter((existing) => existing.id !== project.id),
    ]);
  }, []);

  const confirmProjectOptimistically = useCallback(
    (projectId: string, confirmedProject: Tables<"projects">) => {
      setProjects((prevProjects) =>
        prevProjects.map((project) =>
          project.id === projectId
            ? { ...project, ...confirmedProject, optimisticStatus: "created" }
            : project
        )
      );

      const existingTimer = optimisticStatusTimers.current.get(projectId);
      if (existingTimer) clearTimeout(existingTimer);

      const timer = setTimeout(() => {
        setProjects((prevProjects) =>
          prevProjects.map((project) => {
            if (project.id !== projectId) return project;
            const { optimisticStatus: _optimisticStatus, ...confirmed } = project;
            return confirmed;
          })
        );
        optimisticStatusTimers.current.delete(projectId);
      }, 1600);
      optimisticStatusTimers.current.set(projectId, timer);
    },
    []
  );

  const removeProjectOptimistically = useCallback((projectId: string) => {
    const timer = optimisticStatusTimers.current.get(projectId);
    if (timer) {
      clearTimeout(timer);
      optimisticStatusTimers.current.delete(projectId);
    }
    setProjects((prevProjects) =>
      prevProjects.filter((project) => project.id !== projectId)
    );
  }, []);

  // Optimistically update a single project in the local state
  const updateProjectOptimistically = useCallback((projectId: string, updates: Partial<ProjectSummary>) => {
    setProjects((prevProjects) =>
      prevProjects.map((project) =>
        project.id === projectId ? { ...project, ...updates } : project
      )
    );
  }, []);

  // Silent refresh that doesn't show loading state
  const refreshSilently = useCallback(async () => {
    // Preserve scroll position
    const scrollPosition = window.scrollY || window.pageYOffset;

    let query = supabase
      .from("projects")
      .select(
        `
        id,
        name,
        color,
        budget,
        category_id,
        deal_stage_id,
        deal_stage:deal_stages ( id, name, likelihood_to_close, kind ),
        likelihood_to_close,
        start_date,
        end_date,
        num_weeks,
        office_id,
        client,
        client_id,
        description,
        potential_future_work,
        tech_stack,
        engagement_type,
        team_success_ingredients,
        created_at,
        updated_at,
        office:offices ( name, is_active ),
        project_assignments (
          id,
          project_id,
          required_role_id,
          weekly_hours_allocated,
          is_confirmed,
          notes,
          display_order,
          created_at,
          updated_at,
          assignment_employees (
            employee:employees ( id, name, nickname, role, weekly_capacity_hours )
          )
        )
      `
      );

    // Filter out Lost projects, but always include the focused project from URL
    // Lost projects are excluded client-side after normalization (the deal stage
    // name is no longer a column on projects — it's joined from deal_stages).

    const { data, error: queryError } = await query
      .returns<RawProject[]>()
      .order("start_date", { ascending: true });

    if (queryError) {
      setError(queryError.message ?? "Failed to load projects");
      return;
    }

    const normalized: ProjectSummary[] = (data ?? []).map((project) => {
      const { project_assignments, ...projectFields } = project;
      const sortedAssignments = [...(project_assignments ?? [])].sort((a, b) => {
        const orderDiff = (a.display_order ?? 0) - (b.display_order ?? 0);
        if (orderDiff !== 0) return orderDiff;
        return (a.created_at ?? "").localeCompare(b.created_at ?? "");
      });
      return {
        ...projectFields,
        project_assignments:
          sortedAssignments.map((assignment) => {
            const { assignment_employees, ...assignmentFields } = assignment;
            const employees = (assignment_employees ?? [])
              .map((ae) => ae.employee)
              .filter((emp): emp is AssignmentEmployee => emp !== null)
              .map((emp) => {
                const r = emp.role;
                const primaryRole = Array.isArray(r) ? (r[0] ?? null) : (r ?? null);
                return {
                  id: emp.id,
                  name: emp.name ?? null,
                  nickname: emp.nickname ?? null,
                  role: primaryRole,
                  weekly_capacity_hours: emp.weekly_capacity_hours ?? 40,
                };
              });

            return {
              ...assignmentFields,
              employees,
            };
          }),
      };
    });

    const visible = normalized.filter(
      (p) => p.deal_stage?.kind !== "lost" || p.id === focusedProjectId
    );

    celebrateWonTransitions(visible);
    setProjects((currentProjects) =>
      preservePendingProjects(visible, currentProjects)
    );
    setError(null);

    // Restore scroll position
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
    });
  }, [focusedProjectId, celebrateWonTransitions]);

  useRealtimeRefresh(
    ["projects", "project_assignments", "assignment_employees", "employees"],
    refreshSilently,
    { skipRefetchOnSelfEdit: true }
  );

  return {
    projects,
    loading,
    error,
    refresh: fetchProjects,
    addProjectOptimistically,
    confirmProjectOptimistically,
    removeProjectOptimistically,
    updateProjectOptimistically,
    refreshSilently,
  };
}
